import { SHOW_ALL_ITEMS_QUANTITY, QUANTITY_OPTIONS } from '@/shared/config';
import type { OrderOptions } from '@/shared/lib';

export type RecordSearchType = 'prefix' | 'name';

/**
 * Ordering key of "Por número de revisões".
 *
 * The endpoint counts the revisions behind each row and orders by that count, so
 * this is a request field like the others: the screen never has to hold every
 * revision in memory to sort by how many there are.
 */
export const REVISION_COUNT_SORT_KEY = 'olderRevisionsCount';

export type RecordSortField = 'effectiveDate' | 'name' | 'prefix' | 'olderRevisionsCount';

export type RecordStatusFilter = 'vigente' | 'obsoleto' | 'inativo';

export interface RecordListFilters {
  status?: RecordStatusFilter;
  generatedRecords?: string;
}

export const RECORD_SEARCH_FIELDS: { id: RecordSearchType; name: string }[] = [
  { id: 'prefix', name: 'Registro' },
  { id: 'name', name: 'Título' },
];

export const DEFAULT_RECORD_SEARCH_TYPE: RecordSearchType = 'prefix';

export const DEFAULT_RECORD_QUANTITY = 10;

export const RECORD_SORT_FIELDS: RecordSortField[] = [
  'effectiveDate',
  'name',
  'prefix',
  REVISION_COUNT_SORT_KEY,
];

export const RECORD_LIST_ORDER_OPTIONS: OrderOptions[] = [
  { label: 'Por arquivo mais recente', sort: 'effectiveDate', order: 'desc' },
  { label: 'Por arquivo mais antigo', sort: 'effectiveDate', order: 'asc' },
  { label: 'Por ordem alfabética crescente', sort: 'name', order: 'asc' },
  { label: 'Por ordem alfabética decrescente', sort: 'name', order: 'desc' },
  { label: 'Do menor para o maior', sort: 'prefix', order: 'asc' },
  { label: 'Do maior para o menor', sort: 'prefix', order: 'desc' },
  { label: 'Por número de revisões', sort: REVISION_COUNT_SORT_KEY, order: 'desc' },
];

export const RECORD_STATUS_FILTER_OPTIONS: { id: RecordStatusFilter; name: string }[] = [
  { id: 'vigente', name: 'Vigente' },
  { id: 'obsoleto', name: 'Obsoleto' },
  { id: 'inativo', name: 'Inativo' },
];

/**
 * Generated record ranges from the prototype. A missing `max` is the open upper
 * bound of the last option. The hyphenated serialisation follows the backend
 * range filter pattern, and `30-` overlaps the previous range at exactly 30
 * because the design asks for "De 21 - 30" and "De 30 - Maior quantidade".
 */
export const GENERATED_RECORDS_RANGE_OPTIONS: {
  id: string;
  name: string;
  min: number;
  max?: number;
}[] = [
  { id: '0-10', name: 'De 0 - 10', min: 0, max: 10 },
  { id: '11-20', name: 'De 11 - 20', min: 11, max: 20 },
  { id: '21-30', name: 'De 21 - 30', min: 21, max: 30 },
  { id: '30-', name: 'De 30 - Maior quantidade', min: 30 },
];

const QUANTITY_IDS = QUANTITY_OPTIONS.map((option) => option.id);

const firstQueryValue = (raw: unknown): string | undefined => {
  if (typeof raw === 'string') return raw;
  if (Array.isArray(raw) && typeof raw[0] === 'string') return raw[0];
  return undefined;
};

/**
 * Vue Router hands over an array when the param is repeated in the URL, and the
 * term is used as a string all over the listing. Normalising it at the query
 * boundary keeps `?term=a&term=b` from breaking the first load.
 */
export const parseRecordTerm = (raw: unknown): string => firstQueryValue(raw) ?? '';

export const parseRecordQuantity = (raw: unknown): number => {
  const value = Number(firstQueryValue(raw));
  return QUANTITY_IDS.includes(value) ? value : DEFAULT_RECORD_QUANTITY;
};

export const parseRecordStatusFilter = (raw: unknown): RecordStatusFilter | undefined => {
  const value = firstQueryValue(raw);
  return RECORD_STATUS_FILTER_OPTIONS.some((option) => option.id === value)
    ? (value as RecordStatusFilter)
    : undefined;
};

export const parseGeneratedRecordsRange = (raw: unknown): string | undefined => {
  const value = firstQueryValue(raw);
  return GENERATED_RECORDS_RANGE_OPTIONS.some((option) => option.id === value) ? value : undefined;
};

export const parseRecordListFilters = (query: Record<string, unknown>): RecordListFilters => ({
  status: parseRecordStatusFilter(query.status),
  generatedRecords: parseGeneratedRecordsRange(query.generatedRecords),
});

export const isRecordSortField = (value: string): value is RecordSortField =>
  RECORD_SORT_FIELDS.includes(value as RecordSortField);

export const isShowAllQuantity = (quantity: number) => quantity === SHOW_ALL_ITEMS_QUANTITY;
